import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator,
  ImageOutlined as ImageOutlinedIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateCategory } from '../../redux/actions/posMenu';
import { getTranslationValue } from '../../constants/posMenu';

const SortableItem = ({ item }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        backgroundColor: isDragging ? '#e3f2fd' : '#f5f5f5',
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        '&:hover': {
          backgroundColor: '#eeeeee',
        },
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      {...attributes}
      {...listeners}
    >
      <ListItemIcon>
        <DragIndicator sx={{ color: '#9E9EAD' }} />
      </ListItemIcon>
      <ListItemIcon sx={{ minWidth: 'auto', mr: 2 }}>
        {item?.image?.url ? (
          <Avatar
            src={`${item?.image?.url}?tr=w-40,h-40`}
            alt={getTranslationValue(item?.name)}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: '#e0e0e0',
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: '#e0e0e0',
            }}
          >
            <ImageOutlinedIcon sx={{ color: '#323232' }} />
          </Avatar>
        )}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {getTranslationValue(item?.name)}
          </Typography>
        }
        secondary={
          <Typography variant="body2" color="text.secondary">
            ${(Number(item?.price) || 0).toFixed(2)}
          </Typography>
        }
      />
    </ListItem>
  );
};

const ItemSortModal = ({ open, onClose, category, menuMap }) => {
  const dispatch = useDispatch();
  const [sortedItems, setSortedItems] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize sorted items when modal opens
  useEffect(() => {
    if (open && category?.itemIds) {
      const items = (category.itemIds || [])
        .map((itemId) => menuMap?.items?.[itemId])
        .filter(Boolean);
      setSortedItems(items);
      setHasChanges(false);
    }
  }, [open, category?.itemIds, menuMap]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSortedItems((items) => {
        const oldIndex = items.findIndex((item) => item._id === active.id);
        const newIndex = items.findIndex((item) => item._id === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);
        setHasChanges(true);
        return newOrder;
      });
    }
  }, []);

  const handleSave = async () => {
    if (hasChanges && category) {
      setIsSaving(true);
      const itemIds = sortedItems.map((item) => item._id);
      try {
        await dispatch(
          updateCategory(category._id, {
            itemIds,
          })
        );
        toast.success('Item order saved');
        setHasChanges(false);
        onClose();
      } catch (error) {
        toast.error(error.message || 'Failed to save order');
      } finally {
        setIsSaving(false);
      }
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    if (category?.itemIds) {
      const items = (category.itemIds || [])
        .map((itemId) => menuMap?.items?.[itemId])
        .filter(Boolean);
      setSortedItems(items);
    }
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Sort Items
          </Typography>
          <Typography variant="body2" color="text.secondary">
            in {getTranslationValue(category?.name)}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, overflowX: 'hidden' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Drag and drop items to reorder them. Changes will be saved when you
          click &quot;Save&quot;.
        </Typography>

        {sortedItems.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={sortedItems.map((item) => item._id)}
              strategy={verticalListSortingStrategy}
            >
              <List sx={{ padding: 0, overflowX: 'hidden' }}>
                {sortedItems.map((item) => (
                  <SortableItem key={item._id} item={item} />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No items to sort in this category
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleCancel} variant="outlined" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges || isSaving}
          sx={{
            backgroundColor: hasChanges ? '#1976d2' : undefined,
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemSortModal;

